import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { FolderPlusIcon } from '@heroicons/react/20/solid';
import { i18nConfig } from 'i18n-config';
import { Modal, Button, Anchor, bjToast } from '@components/common';
import { getI18nLinkHref } from '@services/i18nPaths';
import { useSwrBulkSaveToProject } from '@hooks/useSwrApi/endpoints/bulk-operations';
import { useSwrResumeGetOperationJob } from '@hooks/useSwrApi/endpoints/resume-operations';
import { GetSelectedApplicants } from '@type/bulk-operations';
import { BulkSaveToProjectResponse, OperationJob } from '@type/v1-api-types';
import { JobFolderSelector } from '@components/templates/cv/partials/JobFolderSelector';

interface BulkSaveToProjectProps extends GetSelectedApplicants {
  locale?: Locale;
  triggerDisabled?: boolean;
  /**
   * Max number of slugs per batch enforced client-side. Kept in sync with
   * `OperationController::BULK_SAVE_TO_PROJECT_MAX` on the backend.
   */
  maxBatch?: number;
  /**
   * Fired after the user clicks "Add more" from the success view. The /cv/search
   * page uses this to clear the current selection so the recruiter can pick a
   * different set of candidates on top of the same result list.
   */
  onDone?: () => void;
}

/**
 * @description Bulk save-to-project from /cv/search.
 *
 * Opens a modal with `JobFolderSelector` so the recruiter picks an existing
 * recruiting project (job) or creates a new one. On submit, each selected
 * candidate is turned into an Applicant of that project — *no* status
 * transition and *no* interview request; the recruiter refines targets from
 * the project view afterward. See `POST /v1/resume-operation/bulk-save-to-project`
 * for the backend contract.
 */
export const BulkSaveToProject = (props: BulkSaveToProjectProps) => {
  const {
    getSelectedApplicants,
    selectAll,
    locale = i18nConfig.defaultLocale,
    queryParams,
    normalizeSelectedApplicantsForBulkAction,
    triggerDisabled = false,
    maxBatch = 100,
    onDone,
  } = props;

  const { t } = useTranslation('common');

  const [isOpen, setIsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState<OperationJob | undefined>(undefined);
  const [result, setResult] = useState<BulkSaveToProjectResponse | undefined>(undefined);

  // Pre-fill the last-used operation job so the recruiter can save without
  // picking one from scratch every time. Triggered once on first modal open
  // and reused across subsequent opens in the same session; the recruiter
  // can still click Edit inside JobFolderSelector to change it.
  const hasPrefilledRef = useRef(false);
  const { trigger: fetchLastOperationJob } = useSwrResumeGetOperationJob({
    locale,
    successCallback: (response) => {
      if (response?.title) {
        setSelectedJob({
          jobId: response.jobId ?? 0,
          jobSlug: response.jobSlug ?? '',
          title: response.title,
        });
      }
    },
    toastsOptions: { showErrorToast: false },
  });

  const normalize = (raw: string[]) => (
    normalizeSelectedApplicantsForBulkAction ? normalizeSelectedApplicantsForBulkAction(raw) : raw
  );

  // Detect "no filters + selectAll" so we can bail out cleanly — on /cv/search
  // selectAll only spans currently-loaded cards, so an empty selection with
  // selectAll=true means the list itself is empty.
  const paramIsStatus = queryParams
    && Object.keys(queryParams).length === 1
    && queryParams?.['status' as keyof typeof queryParams];
  const hasFiltersApplied = queryParams
    ? Object.keys(queryParams).length > 0 && !paramIsStatus
    : false;

  const closeModal = () => {
    setIsOpen(false);
    setResult(undefined);
    setSelectedJob(undefined);
  };

  const handleAddMore = () => {
    closeModal();
    if (onDone) onDone();
  };

  const openModal = () => {
    const rawSlugs = normalize(getSelectedApplicants());
    if (rawSlugs.length === 0 && !(selectAll && !hasFiltersApplied)) {
      bjToast.error(t('cv.action.bulk.select_cv'));
      return;
    }
    if (rawSlugs.length > maxBatch) {
      bjToast.error(t('cv.action.bulk.save_to_project.cap_exceeded', { max: maxBatch }));
      return;
    }
    setIsOpen(true);
    if (!hasPrefilledRef.current && !selectedJob) {
      hasPrefilledRef.current = true;
      void fetchLastOperationJob();
    }
  };

  const { trigger, isMutating } = useSwrBulkSaveToProject({
    locale,
    onSuccess: (response) => {
      if (response) {
        setResult(response);
      }
    },
  });

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const rawSlugs = normalize(getSelectedApplicants());
    if (rawSlugs.length === 0 || rawSlugs.length > maxBatch) {
      bjToast.error(t('cv.action.bulk.save_to_project.cap_exceeded', { max: maxBatch }));
      return;
    }
    const jobId = selectedJob?.jobId && selectedJob.jobId > 0 ? selectedJob.jobId : undefined;
    const jobTitle = !jobId && selectedJob?.title ? selectedJob.title : undefined;
    if (!jobId && !jobTitle) {
      bjToast.error(t('cv.action.bulk.save_to_project.pick_job'));
      return;
    }
    void trigger({
      userSlugs: rawSlugs,
      ...(jobId ? { jobId } : {}),
      ...(jobTitle ? { jobTitle } : {}),
    });
  };

  const summary = useMemo(() => {
    if (!result) return null;
    const buckets: { key: keyof BulkSaveToProjectResponse; labelKey: string; defaultLabel: string }[] = [
      { key: 'succeeded', labelKey: 'cv.action.bulk.save_to_project.result.succeeded', defaultLabel: 'Saved to the project' },
      { key: 'promoted', labelKey: 'cv.action.bulk.save_to_project.result.promoted', defaultLabel: 'Promoted from suggestions' },
      { key: 'alreadyApplicant', labelKey: 'cv.action.bulk.save_to_project.result.alreadyApplicant', defaultLabel: 'Already in the project (skipped)' },
      { key: 'skippedUnknown', labelKey: 'cv.action.bulk.save_to_project.result.skippedUnknown', defaultLabel: 'Unknown candidates (skipped)' },
      { key: 'failed', labelKey: 'cv.action.bulk.save_to_project.result.failed', defaultLabel: 'Failed' },
    ];
    return buckets.filter(({ key }) => Number(result[key] ?? 0) > 0);
  }, [result]);

  return (
    <>
      <Button
        styling="text"
        size="sm"
        color="ink"
        className="grow px-4"
        disabled={triggerDisabled}
        onClick={openModal}
        aria-label={t('cv.action.bulk.save_to_project.trigger', 'Save to project')}
      >
        <FolderPlusIcon className="mr-2 size-5 sm:mr-1" />
        <span>{t('cv.action.bulk.save_to_project.trigger', 'Save to project')}</span>
      </Button>

      <Modal isOpen={isOpen} onClose={closeModal} size="sm" className="!overflow-visible">
        {result ? (
          <>
            <Modal.Header onClose={closeModal}>
              {t('cv.action.bulk.save_to_project.result.title', 'Candidates saved to the project')}
            </Modal.Header>
            <Modal.Body className="bg-surface-100">
              <ul className="space-y-1 text-sm">
                {summary && summary.length > 0 ? summary.map(({ key, labelKey, defaultLabel }) => (
                  <li key={key} className="flex items-center justify-between gap-4">
                    <span className="text-ink-medium">{t(labelKey, defaultLabel)}</span>
                    <span className="font-semibold text-ink">{String(result[key] ?? 0)}</span>
                  </li>
                )) : (
                  <li className="text-ink-medium">{t('cv.action.bulk.save_to_project.result.empty', 'No changes')}</li>
                )}
              </ul>
            </Modal.Body>
            <Modal.Footer className="flex justify-end gap-2">
              {result.jobSlug && (
                <Anchor
                  // `guide=save-to-project` triggers the onboarding banner on
                  // the applicants list that walks the recruiter toward the
                  // Intervievează bulk action — the intended follow-up step
                  // after saving talents to a project.
                  href={getI18nLinkHref(`/job/${result.jobSlug}/applicants?guide=save-to-project`, locale)}
                  styling="outline"
                  size="sm"
                  rounding="full"
                  color="light"
                >
                  {t('cv.action.bulk.save_to_project.result.go_to_project', 'Go to the project')}
                </Anchor>
              )}
              <Button
                type="button"
                color="secondary"
                rounding="full"
                size="sm"
                onClick={handleAddMore}
              >
                {t('cv.action.bulk.save_to_project.result.add_more', 'Add more')}
              </Button>
            </Modal.Footer>
          </>
        ) : (
          <form noValidate onSubmit={handleSubmit}>
            <Modal.Header onClose={closeModal}>
              {t('cv.action.bulk.save_to_project.title', 'Save candidates to a recruiting project')}
            </Modal.Header>
            <Modal.Body className="bg-surface-100">
              <p className="mb-4 text-sm text-ink-medium">
                {t(
                  'cv.action.bulk.save_to_project.description',
                  'The selected candidates will be saved to this recruiting project. You will be able to refine your targets there.',
                )}
              </p>
              <JobFolderSelector
                selectedJobFolder={selectedJob}
                sendJobFolderInfoToParent={(item) => setSelectedJob(item)}
              />
            </Modal.Body>
            <Modal.Footer className="flex justify-end">
              <Button
                type="submit"
                color="secondary"
                rounding="full"
                size="sm"
                className="!px-7"
                isLoading={isMutating}
                disabled={!selectedJob}
              >
                <FolderPlusIcon className="mr-1.5 size-5" />
                {t('cv.action.bulk.save_to_project.submit', 'Save')}
              </Button>
            </Modal.Footer>
          </form>
        )}
      </Modal>
    </>
  );
};
